import React from 'react';
import type { SelectOption } from './Select';
import { isActiveUser } from '../../utils/activeUsers';

// A person in a staff list (marketing staff, executives, team members).
export interface FilterPerson {
  mobile: string;
  first_name?: string;
  last_name?: string;
  name?: string;
  is_active?: boolean | null;
}

export const personName = (p: FilterPerson): string =>
  (p.name || [p.first_name, p.last_name].filter(Boolean).join(' ')).trim();

// One option row: the name, the mobile below it (with `detail`, e.g. the role,
// after it), and -- when `active` is given -- Active / Inactive on the right.
export const StaffOptionLabel: React.FC<{ name: string; mobile: string; detail?: string; active?: boolean }> = ({ name, mobile, detail, active }) => (
  <span className="flex items-center justify-between gap-3 w-full normal-case">
    <span className="flex flex-col min-w-0 leading-tight">
      <span className="truncate font-medium">{name || mobile}</span>
      {(name || detail) && (
        <span className="truncate text-[11px] opacity-60 font-normal">
          {name ? mobile : ''}{name && detail ? ' · ' : ''}{detail}
        </span>
      )}
    </span>
    {active !== undefined && (
      <span className={`shrink-0 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${active
        ? 'bg-emerald-50 text-emerald-600 border-emerald-200/50'
        : 'bg-red-50 text-red-600 border-red-200/50'}`}>
        {active ? 'Active' : 'Inactive'}
      </span>
    )}
  </span>
);

// A person as a dropdown option. Pickers list only active people (activeOnly),
// so they leave `status` off; filters set it.
export const personOption = (p: FilterPerson, opts: { detail?: string; status?: boolean } = {}): SelectOption => ({
  value: p.mobile,
  label: <StaffOptionLabel name={personName(p)} mobile={p.mobile} detail={opts.detail || undefined}
    active={opts.status ? isActiveUser(p) : undefined} />,
});

// Filters keep deactivated people (unlike pickers, see utils/activeUsers):
// their past orders and visits are still theirs to filter by.
export const staffFilterOption = (p: FilterPerson): SelectOption => personOption(p, { status: true });

// The closed dropdown shows just the name: a person option's label is the
// two-line row above, while plain-text options ("All", "None") keep their text.
export const staffFilterValue = (people: FilterPerson[]) => (selected?: SelectOption): React.ReactNode => {
  if (!selected) return undefined;
  if (typeof selected.label === 'string') return selected.label;
  const p = people.find((x) => x.mobile === selected.value);
  return p ? (personName(p) || p.mobile) : selected.value;
};
